import VampScopeBar from "./VampScopeBar";
import { VampInfoHint } from "./VampTooltip";
import {
  VAMP_EXCESSIVE_PCT,
  VAMP_RATE_BAR_MAX_PCT,
  step1Text,
  step2Text,
  type VampMonthContext,
  type VampReading,
} from "./vampStanding";

interface VampScoringStepsProps {
  reading: VampReading;
  ctx: VampMonthContext;
}

const STEP2_TIP =
  "The bar is this CAID’s rate on a scale to 4%, and the notch is Visa’s 1.5% line. Green means Visa is scoring this CAID and it sits under the line. Red means it is at or over. Grey means Visa is not scoring it this month, either because it has not reached 1,500 events or because no settled volume is recorded to rate against.";

const ROW = "grid grid-cols-[26px_minmax(0,1fr)_150px] items-center gap-x-3 px-3.5 py-[11px]";
const NOTCH_LEFT = `${(VAMP_EXCESSIVE_PCT / VAMP_RATE_BAR_MAX_PCT) * 100}%`;

function StepMark({ n, muted }: { n: number; muted: boolean }) {
  return (
    <div className={`flex h-[22px] w-[22px] items-center justify-center rounded-full text-[11px] font-bold ${muted ? "bg-gray-50 text-gray-300" : "bg-gray-100 text-gray-500"}`}>
      {n}
    </div>
  );
}

export default function VampScoringSteps({ reading, ctx }: VampScoringStepsProps) {
  const { totals, unmatched, state, scored, rate } = reading;
  const rateWidth = unmatched ? 0 : Math.min(100, (rate / VAMP_RATE_BAR_MAX_PCT) * 100);
  const rateColor = !scored ? "bg-gray-300" : rate >= VAMP_EXCESSIVE_PCT ? "bg-error-500" : "bg-success-500";
  const when = ctx.isCurrent ? "so far this month" : "in this month";
  const step1Tip = `The bar runs to 1,500 events, the count Visa needs before it scores this CAID at all. Solid blue is what has been recorded ${when}. Pale blue is where the month ends if the current pace holds. Grey is the distance still left to the threshold.`;

  return (
    <div className="mt-4 rounded-xl border border-gray-100">
      <div className={`${ROW} rounded-t-[11px] bg-gray-25`}>
        <StepMark n={1} muted={false} />
        <div>
          <span className="flex items-center gap-[5px] text-[13px] font-semibold leading-[19px] text-gray-900">
            Will Visa score this?
            <VampInfoHint text={step1Tip} placement="bottom-start" width={292} />
          </span>
          <div className="text-theme-xs text-gray-500">{step1Text(state, unmatched)}</div>
        </div>
        <VampScopeBar events={totals.events} elapsed={ctx.elapsed} label="1,500 events" />
      </div>

      <div className={`${ROW} border-t border-gray-100`}>
        <StepMark n={2} muted={!scored} />
        <div>
          <span className={`flex items-center gap-[5px] text-[13px] font-semibold leading-[19px] ${scored ? "text-gray-900" : "text-gray-400"}`}>
            Is the VAMP rate excessive?
            <VampInfoHint text={STEP2_TIP} placement="bottom-start" width={292} />
          </span>
          <div className="text-theme-xs text-gray-500">{step2Text(reading)}</div>
        </div>
        <div>
          <div className="relative h-2 rounded bg-gray-100">
            <div className={`absolute left-0 top-0 h-2 rounded ${rateColor}`} style={{ width: `${rateWidth}%` }} />
            <div className="absolute -top-[3px] h-3.5 w-0.5 rounded-[1px] bg-error-500" style={{ left: NOTCH_LEFT }} />
          </div>
          <div className="relative mt-1 h-[15px] text-[11px] leading-[15px] text-gray-400 tabular-nums">
            <span className="absolute -translate-x-1/2" style={{ left: NOTCH_LEFT }}>
              1.5%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
